import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, Observer, of, Subject } from 'rxjs';
import { SoapNote, SnomedCodes, DraftNote, VitalBody, ResponseObject, ResponseValueType, GeneratedSoapNoteResponse, GenerateVitalsResponse } from '../../models/dictation.interface';
import { SaveSoapResponse } from '../../models/response.interface';
import { API_ROUTES } from '../../util/route.constants';

import { StorageService } from "../storage-service/storage.service";
import { CVA_CONSTANTS, StorageKeys } from "../../util/constants";
import { ISelectedAppointment } from '../../models/storage.interface';
import { soapTemplateType, VitalResponse } from '../../util/soap-config.constants';
import { IAppointment } from '../../models/appointment.interface';
import { Identifier, TemplateCategory } from '../../models/template.model';
import { TemplateListing } from '../../models/manage-template.interface';

import { ConsultationRecommendationService } from '../consultation-recommendation/consultation-recommendation.service';
import { TimerService } from '../timer/timer.service';

@Injectable({
  providedIn: 'root'
})
export class SoapServiceService {
  refineNote(payload: { prompt: string; target: string; content: string }) {
    return this.http.post<{ response: { refined_content?: string; error?: string; suspicious_patterns?: string[] } }>(
      `${environment.cva_url}${API_ROUTES.AI_PROXY}${API_ROUTES.REFINE_NOTE}`, payload,
    );
  }
  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService,
    private readonly recommendationService: ConsultationRecommendationService,
    private readonly timerService: TimerService
  ) { }

  public openHistoryPopUp$ = new Subject<string>();
  public closeHistoryPopUp$ = new Subject();
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  public vitalsUpdated$ = new Subject<{ appointmentRefId: string; vitals: any; identifier: string }>();
  private readonly breakLine = '\n';
  private readonly tab = '  ';

  getSnomedData(category: string): Observable<Array<SnomedCodes>> {
    return new Observable<Array<SnomedCodes>>((observer: Observer<Array<SnomedCodes>>) => {
      const url = `${environment.cva_url}${API_ROUTES.SNOMED_CODE}?category=${category}`;
      this.http.get<{ response: SnomedCodes }>(url).subscribe({
        next: (res) => {
          if (res.response && Array.isArray(res.response)) {
            observer.next(res.response);
          } else {
            observer.error(res);
          }
        },
        error: (e) => {
          observer.error(e);
        }
      });
    });
  }

  saveSoapNotes(body: SoapNote): Observable<SoapNote> {
    return new Observable<SoapNote>((observer: Observer<SoapNote>) => {
      const url = environment.cva_url + "/soap-note";
      this.http.post<SaveSoapResponse>(url, body).subscribe({
        next: (data: SaveSoapResponse) => {
          if (data?.response) {
            observer.next(data?.response);
          }
          else {
            of(data);
          }
        },
        error: (e) => {
          observer.error(e);
        }
      });
    });
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  setAppointmentAndDraftedNotes(referenceId: string, notes: DraftNote = {}, transcription: string = '', templateType: string = "", tabsData: any = []): void {
    const record = { referenceId, notes, transcription, templateType, tabsData };
    this.storageService.setItem<ISelectedAppointment>(StorageKeys.SELECTED_APPOINTMENT, record);
  }

  getAppointmentAndDraftedNotes(): ISelectedAppointment | null {
    return this.storageService.getItem<ISelectedAppointment>(StorageKeys.SELECTED_APPOINTMENT);
  }

  setCurrentAppointment(appointment: IAppointment): void {
    this.storageService.setItem<IAppointment>(StorageKeys.CURRENT_APPOINTMENT, appointment);
  }

  getCurrentAppointment(): IAppointment | null {
    return this.storageService.getItem<IAppointment>(StorageKeys.CURRENT_APPOINTMENT);
  }

  removeAppointmentAndDraftedNotes(): void {
    this.storageService.removeItem(StorageKeys.SELECTED_APPOINTMENT);
  }

  removeCurrentAppointment(): void {
    this.storageService.removeItem(StorageKeys.CURRENT_APPOINTMENT);
  }

  getAISoapNotes(
    template: TemplateListing,
    identifier: string,
    conversation: string,
    speechServiceTime: string = this.timerService.getElapsedTimeFormatted(),
    patientName?: string,
    practitionerName?: string,
    patientId?: string,
    patientHistory?: number,
    classifications?: string[] | string
  ): Observable<GeneratedSoapNoteResponse> {
    return new Observable((observer: Observer<GeneratedSoapNoteResponse>) => {
      const url = environment.cva_url + "/ai-proxy/generate";
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      const requestBody: any = {
        conversation: !this.containsTimestamp(conversation) ? '00:01 ' + conversation : conversation,
        identifier,
        speechServiceTime,
        isCustomTemplate: template.isCustomTemplate,
        patientName: patientName ?? '',
        practitionerName: practitionerName ?? '',
        patientId: patientId ?? '',
        patientHistory: patientHistory ?? 0,
        classifications: classifications ?? []
      };

      if (identifier !== Identifier.SOAP && identifier !== Identifier.ISSUE_LIST) {
        requestBody['patientName'] = this.capitalizeFirstLetterOfEachWord(patientName ?? '');
        requestBody['practitionerName'] = this.capitalizeFirstLetterOfEachWord(practitionerName ?? '');
      }

      if (template.isCustomTemplate && template.category === TemplateCategory.NOTE) {
        requestBody.vitals = "false"
      }

      //make api call for all recommendation services
      this.recommendationService.triggerGenerateNote(
        JSON.parse(JSON.stringify({
          transcript: requestBody.conversation,
          timestamp: Date.now(),
          speechServiceTime
        }))
      );

      this.http.post<{ "response": GeneratedSoapNoteResponse }>(url, requestBody).subscribe({
        next: (data: { "response": GeneratedSoapNoteResponse }) => {
          if (data.response) {
            //let recommendation components knows that notes are generated and they can now render the data
            this.recommendationService.generateNotesReady$.next(true);
            observer.next(data.response);
            observer.complete();
          } else {
            observer.error(data)
            this.recommendationService.generateNotesFailed$.next(true);
          }
        },
        error: (e) => {
          observer.error(e?.error?.error);
          this.recommendationService.generateNotesFailed$.next(true);
        }
      });
    });
  }

  generateVitals(transcript: string): Observable<GenerateVitalsResponse> {
    return new Observable((observer: Observer<GenerateVitalsResponse>) => {
      const url = environment.cva_url + "/ai-proxy/generate-vitals";
      const requestBody = {
        transcript
      };

      this.http.post<{ "response": GenerateVitalsResponse }>(url, requestBody).subscribe({
        next: (data: { "response": GenerateVitalsResponse }) => {
          if (data.response) {
            observer.next(data.response);
            observer.complete();
          } else {
            observer.error(data);
          }
        },
        error: (e) => {
          observer.error(e?.error?.error);
        }
      });
    });
  }

  saveVitals(vitals: VitalBody) {
    return new Observable<{ "response": VitalResponse }>((observer) => {
      const url = environment.cva_url + `/observation/patient/${vitals.patientId}`;
      this.http.post<{ "response": VitalResponse }>(url, vitals).subscribe({
        next: (data) => {
          if (data) {
            observer.next(data)
          } else {
            observer.error(data)
          }
        },
        error: (e) => {
          observer.error(e?.error?.error);
        }
      });
    });
  }

  transformToString(value: ResponseValueType, level: number = 0): string {
    const indent = `${this.tab}`.repeat(level);

    if (typeof value === 'string') {
      return this.doesNotIncludeNotMention(value)
        ? `${indent}- ${value}`
        : '';
    }

    if (typeof value === 'number') {
      return `${indent}- ${value}`;
    }

    if (Array.isArray(value)) {
      if (value.every(item => typeof item === 'string' || typeof item === 'number')) {
        return value.map(val => this.doesNotIncludeNotMention(String(val)) ? `${indent}- ${val}` : '').join(this.breakLine);
      }
      return this.formatArrayOfObject(value, level);
    }

    if (value && typeof value === 'object') {
      return this.formatObject(value, level);
    }

    return ''; // For any other types or undefined values
  }

  formatObject(obj: ResponseObject, level: number = 0): string {
    const indent = `${this.tab}`.repeat(level);
    return Object.entries(obj)
      .map(([key, value]) => {
        //return the key value pair with indentation in a single line
        if (typeof value === 'string' || typeof value === 'number') {
          if (this.doesNotIncludeNotMention(String(value)) && (["Name", "Instructions", "Dosage"].indexOf(key) > -1)) {
            return `- ${this.formatKeyText(key)}: ${value}`;
          } else {
            return ''
          }
        }
        //create an hierarchy
        const formattedValue = this.transformToString(value, level + 1);
        return formattedValue ? `${indent}- ${this.formatKeyText(key)}:${this.breakLine}${formattedValue}` : '';
      })
      .filter(line => line) // filter out empty strings
      .join(this.breakLine);
  }

  formatArrayOfObject(records: ResponseValueType[], level: number = 0): string {
    return records
      .map(record => {
        if (typeof (record) === 'string') {
          return this.doesNotIncludeNotMention(record) ? record : '';
        } else {
          return this.transformToString(record, level + 1) + this.breakLine
        }
      })
      .filter(part => part) // filter out empty strings
      .join(this.breakLine);
  }

  /**
   * Replaces underscores with spaces and capitalizes the first character of a string.
   * @param str - The input string to transform.
   * @returns A new string with underscores replaced by spaces and the first character capitalized.
   */
  formatKeyText(str: string): string {
    if (!str) return str;
    const replacedString = str.replace(/_/g, ' ');
    return replacedString.charAt(0).toUpperCase() + replacedString.slice(1);
  }

  containsTimestamp(text: string): boolean {
    // Regex for matching time format: HH:mm, HH:mm:ss, or HH:mm:ss:SS
    const regex = /(?:[01]?\d|2[0-3]):[0-5]?\d(?::[0-5]?\d(?:\.\d{1,3})?)?/;
    return regex.test(text);
  }

  doesNotIncludeNotMention(value: string) {
    return value && !value.toLowerCase().includes(CVA_CONSTANTS.MESSAGE.NOT_MENTION);
  }


  capitalizeFirstLetterOfEachWord(value: string): string {
    if (!value) return value;  // Handle null, undefined, or empty string

    return value
      .split(' ')  // Split the string into words by space
      .map(word => {
        // Capitalize the first letter and make the rest lowercase
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');  // Join the words back into a single string
  }

  openWebPortal(redirectTo = '') {
    const id_token = localStorage.getItem('idToken');
    const auth_token = localStorage.getItem('authToken');
    const facilityId = localStorage.getItem('facilityCode');
    if (id_token && auth_token) {
      const callbackUrl = `${environment.cvaUI_url}/auth/callback#idToken=${id_token}&authToken=${auth_token}${redirectTo ? '&redirectTo=' + redirectTo : ''}${facilityId ? '&facilityCode=' + facilityId : ''}`;
      window.open(callbackUrl, "_blank");
    }
  }

  getTemplate(templateType: string, noteTemplateListing: TemplateListing[]): {
    templateType: string, selectedTemplate: TemplateListing
  } | null {
    if (templateType && noteTemplateListing?.length) {
      let selectedTemplate = noteTemplateListing.find(template => template.identifier === templateType) as TemplateListing;
      let identifier = templateType;
      if (!selectedTemplate) {
        selectedTemplate = noteTemplateListing.find(template => template.identifier === soapTemplateType.Lite) as TemplateListing;
        identifier = soapTemplateType.Lite;
      }
      return {
        templateType: identifier,
        selectedTemplate: selectedTemplate
      };
    } else {
      return null;
    }
  }
}

